import React, { useState, useEffect } from "react";
import "../styles/Settings.scss";
import Modal from "react-modal";
import Select from "react-select";
import { getUsersByRoleId, getRecruiterReport } from "../services/api";
import ExcelJS from "exceljs";

Modal.setAppElement("#root");

const ITEMS_PER_PAGE = 10;

function RecruiterReport() {
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState([]);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // For download the data
  // const handleExport = () => {
  //   const csv = [
  //     "S.NO, Recruiter Name, Assigned Requisitions, No. of Requirements worked",
  //     ...reportData.map(
  //       (item, index) =>
  //         `${index + 1},${selectedRecruiter?.label || ""},${
  //           item.assigned || 0
  //         },${item.working_on || 0}`
  //     ),
  //   ].join("\n");
  //   const blob = new Blob([csv], { type: "text/csv" });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement("a");
  //   a.href = url;
  //   a.download = "RecruiterReport.csv";
  //   a.click();
  //   URL.revokeObjectURL(url);
  // };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Recruiter Report");

    // Add header row
    worksheet.columns = [
      { header: "S.NO", key: "sno" },
      { header: "Recruiter Name", key: "recruiter_name" },
      { header: "Assigned Requisitions", key: "assigned" },
      { header: "No. of Requirements worked", key: "working_on" },
    ];

    // Add data rows
    reportData.forEach((item, index) => {
      worksheet.addRow({
        sno: index + 1,
        recruiter_name: selectedRecruiter?.label || "--",
        assigned: item.assigned || 0,
        working_on: item.working_on || 0,
      });
    });

    // Create a Blob from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "RecruitersReport.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
  };

  // Reset to page 1 on search
  React.useEffect(() => {
    setPage(1);
  }, [selectedRecruiter, startDate, endDate, reportData.length]);

  // for recruiters filter along with pagination
  const filteredData = reportData;
  const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // Fetch recruiters for role_id 4 (Recruiter)
  useEffect(() => {
    setLoading(true);
    const fetchRecruiters = async () => {
      setRecruiters([]);
      try {
        const res = await getUsersByRoleId(4);
        if (res?.status === "S" && Array.isArray(res.result_info)) {
          setRecruiters(res.result_info);
        }
      } catch (error) {
        console.error("Error fetching recruiters:", error);
        setError("Failed to fetch recruiters");
      } finally {
        setLoading(false);
      }
    };
    fetchRecruiters();
  }, []);

  // To get recruiter report data
  const fetchRecruiterReport = async () => {
    setReportData([]);
    setLoading(true);
    setError("");
    try {
      const res = await getRecruiterReport(
        selectedRecruiter?.value || "",
        startDate,
        endDate
      );
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        // Convert the single object to an array for consistency
        setReportData(res.result_info);
      } else {
        setError(res?.message || "Failed to fetch report data");
      }
    } catch (err) {
      setError("Failed to fetch report data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch report data when recruiter or dates change
  useEffect(() => {
    fetchRecruiterReport();
  }, [selectedRecruiter, startDate, endDate]);

  // Convert recruiters to react-select options
  const recruiterOptions = recruiters.map((recruiter) => ({
    value: recruiter.id,
    label: recruiter.name,
  }));

  return (
    <div className="settings-page">
      <div className="settings-header-row">
        <div className="requisitions-title-group">
          <h1>Recruiters Report</h1>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="export-btn"
            onClick={handleExport}
            // disabled={!selectedRecruiter || reportData.length === 0}
          >
            Export
          </button>
        </div>
      </div>
      <div className="settings-table-controls">
        <div className="filters-row">
          <div className="select-wrapper">
            <Select
              placeholder="Select a Recruiter..."
              value={selectedRecruiter}
              onChange={setSelectedRecruiter}
              options={recruiterOptions}
              styles={{
                control: (base) => ({
                  ...base,
                  minWidth: 220,
                  borderRadius: 6,
                  fontSize: "1rem",
                  background: "#fff",
                  border: "1.5px solid #d1d5db",
                }),
                menu: (base) => ({ ...base, zIndex: 20 }),
              }}
              isClearable
              isSearchable
            />
          </div>
          <div className="date-filters">
            <div className="date-input-wrapper">
              {!startDate && (
                <span className="placeholder-text">Start Date</span>
              )}
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input"
              />
            </div>
            <div className="date-input-wrapper">
              {!endDate && <span className="placeholder-text">End Date</span>}
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="date-input"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="settings-table-wrapper">
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading report data...
          </div>
        ) : (
          <table className="settings-table">
            <thead>
              <tr>
                <th>S.NO</th>
                <th>Recruiter Name</th>
                <th>Assigned Requisitions</th>
                <th>No. of Requirements worked</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center" }}>
                    {reportData.length === 0
                      ? "No report data found"
                      : "No data matches your date filters"}
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={index}>
                    <td>{(page - 1) * ITEMS_PER_PAGE + index + 1}</td>
                    <td>{item.name || "-"}</td>
                    <td>{item.assigned || 0}</td>
                    <td>{item.working_on || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination Controls */}
      {pageCount > 1 && (
        <div className="settings-pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            &lt;
          </button>
          {Array.from({ length: pageCount }, (_, i) => (
            <button
              key={i + 1}
              className={"pagination-btn" + (page === i + 1 ? " active" : "")}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === pageCount}
          >
            &gt;
          </button>
        </div>
      )}
    </div>
  );
}

export default RecruiterReport;
